import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import { formatCurrency } from "@/lib/formatters";

export const useGiftNotifications = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel('gift-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'gifts'
        },
        async (payload) => {
          const newGift = payload.new;
          
          // Get current user
          const { data: { user } } = await supabase.auth.getUser();
          
          // Only show notification if this gift is for the current user
          if (user && newGift.recipient_id === user.id) {
            // Fetch sender info for the notification
            const senderName = newGift.is_anonymous 
              ? "Someone" 
              : await getSenderName(newGift.sender_id);
            
            // Show toast notification
            toast.success(
              `New Gift Received!`,
              {
                description: `${senderName} sent you ${formatCurrency(newGift.amount, newGift.currency)}`,
                icon: <Gift className="h-5 w-5" />,
                duration: 5000,
              }
            );
            
            // Invalidate queries to refresh the dashboard
            queryClient.invalidateQueries({ queryKey: ["received-gifts"] });
            queryClient.invalidateQueries({ queryKey: ["gift-stats"] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
};

// Helper function to get sender name
// Helper function to get sender name (uses safe_users view for security)
const getSenderName = async (senderId: string): Promise<string> => {
  try {
    const { data } = await supabase
      .from("safe_users")
      .select("name")
      .eq("id", senderId)
      .single();
    
    return data?.name || "Someone";
  } catch {
    return "Someone";
  }
};
